<!-- Page Header -->
<app-page-header
  [title]="'Billing Service'"
  [subtitle]="'Manage trip billing and generate invoices.'"
  [showDate]="true"
></app-page-header>

<div class="billing-container">
  <!-- Loading Indicator -->
  <div class="loading-container" *ngIf="loading">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading...</p>
  </div>

  <!-- Error Message -->
  <div class="error-container" *ngIf="error && !loading">
    <mat-icon class="error-icon">error</mat-icon>
    <p>{{ error }}</p>
    <button mat-button (click)="loadFilterSuggestions()">Retry</button>
  </div>

  <!-- Main Content -->
  <div class="billing-content" *ngIf="!loading && !error">
    <!-- Filter Trips Section -->
    <div class="filter-section">
      <div class="section-header">
        <h3>Filter Trips</h3>
        <p>
          Select filters to find specific trips (Type is required; Vendor is
          required only if Type is VENDOR)
        </p>
      </div>

      <form [formGroup]="filterForm" class="filter-form">
        <div class="form-row">
          <div class="form-field">
            <app-mat-dropdown
              formControlName="type"
              label="Type"
              [options]="typeOptions"
              placeholder="Select Type"
              [required]="true"
              [prefix]="{
                type: 'icon',
                value: 'category',
                size: 20
              }"
              (selectionChange)="onTypeChange()"
              (blur)="onTypeBlur()"
              errorMessage="Type is required"
            >
            </app-mat-dropdown>
          </div>

          <div class="form-field">
            <app-mat-dropdown
              formControlName="vendorId"
              label="Vendor"
              [options]="getVendorOptions()"
              [searchable]="true"
              placeholder="Select Vendor"
              [required]="filterForm.get('type')?.value === 'VENDOR'"
              [prefix]="{
                type: 'icon',
                value: 'business',
                size: 20
              }"
              (selectionChange)="onVendorChange()"
              [errorMessage]="
                filterForm.get('type')?.value === 'VENDOR'
                  ? 'Vendor is required when type is VENDOR'
                  : ''
              "
            >
            </app-mat-dropdown>
          </div>

          <div class="form-field">
            <app-mat-dropdown
              formControlName="routeId"
              label="Route Name"
              [options]="getRouteOptions()"
              [searchable]="true"
              placeholder="Select Route"
              [prefix]="{
                type: 'icon',
                value: 'route',
                size: 20
              }"
              (selectionChange)="onRouteChange()"
            >
            </app-mat-dropdown>
          </div>
          <div class="form-row">
            <div class="form-field">
              <app-mat-dropdown
                formControlName="vehicleId"
                label="Vehicle Number"
                [options]="getVehicleOptions()"
                [searchable]="true"
                placeholder="Select Vehicle"
                [prefix]="{
                  type: 'icon',
                  value: 'local_shipping',
                  size: 20
                }"
                (selectionChange)="onVehicleChange()"
              >
              </app-mat-dropdown>
            </div>
          </div>

          <div class="form-field">
            <app-mat-dropdown
              formControlName="vehicleSize"
              label="Vehicle Size"
              [options]="vehicleSizes"
              placeholder="Select Vehicle Size"
              [prefix]="{
                type: 'icon',
                value: 'straighten',
                size: 20
              }"
              (selectionChange)="onVehicleSizeChange()"
            >
            </app-mat-dropdown>
          </div>

          <div class="form-field">
            <app-mat-dropdown
              formControlName="driverId"
              label="Driver"
              [options]="getDriverOptions()"
              [searchable]="true"
              placeholder="Select Driver"
              [prefix]="{
                type: 'icon',
                value: 'person',
                size: 20
              }"
              (selectionChange)="onDriverChange()"
            >
            </app-mat-dropdown>
          </div>

          <div class="form-field">
            <app-mat-date-picker
              formControlName="fromDate"
              [control]="filterForm.get('fromDate')"
              label="From Date"
              placeholder="Select start date"
              [maxDate]="getMaxFromDate()"
              (dateChange)="onFromDateChange($event)"
            >
            </app-mat-date-picker>
          </div>

          <div class="form-field">
            <app-mat-date-picker
              formControlName="toDate"
              [control]="filterForm.get('toDate')"
              label="To Date"
              placeholder="Select end date"
              [minDate]="getMinToDate()"
              [maxDate]="getMaxToDate()"
              (dateChange)="onToDateChange($event)"
            >
            </app-mat-date-picker>
          </div>
        </div>

        <div class="filter-actions">
          <button
            type="button"
            mat-raised-button
            color="primary"
            (click)="applyFilters()"
            [disabled]="loadingFilters"
          >
            <mat-icon *ngIf="!loadingFilters">filter_list</mat-icon>
            <mat-spinner *ngIf="loadingFilters" diameter="20"></mat-spinner>
            {{ loadingFilters ? "Loading..." : "Apply Filters" }}
          </button>

          <button type="button" mat-button (click)="clearFilters()">
            <mat-icon>clear</mat-icon>
            Clear Filters
          </button>
        </div>
      </form>
    </div>

    <!-- Trip Data Section -->
    <div class="trip-data-section">
      <div class="section-header">
        <div class="header-content">
          <div class="header-info">
            <h3>Trip Data ({{ totalElements }} trips)</h3>
            <p>Select trips to generate invoices</p>
          </div>
          <div class="next-button" *ngIf="getSelectedCount() > 0">
            <button
              type="button"
              mat-raised-button
              color="primary"
              (click)="onNextClick()"
            >
              <mat-icon>arrow_forward</mat-icon>
              Next ({{ getSelectedCount() }})
            </button>
          </div>
        </div>
      </div>

      <div class="table-container">
        <app-mat-table
          [columns]="columns"
          [dataSource]="trips"
          [displayedColumns]="displayedColumns"
          [selectable]="true"
          [pageSize]="pageSize"
          [pageSizeOptions]="pageSizeOptions"
          [showFirstLastButtons]="true"
          [loading]="loading"
          [noDataMessage]="'No trips found'"
          [stickyHeader]="true"
          [height]="'auto'"
          [width]="'100%'"
          [totalCount]="totalElements"
          (selectionChange)="onSelectionChange($event)"
          (rowClick)="onRowClick($event)"
          (sortChange)="onSortChange($event)"
          (pageChange)="onPageChange($event)"
        >
        </app-mat-table>
      </div>
    </div>
  </div>
</div>
